import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import {
  Modal,
  Select,
  Group,
  Stack,
  Text,
  Button,
  Checkbox,
  Divider,
  Badge,
  Loader,
  Alert,
  Box,
} from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { useForm } from "@mantine/form";
import {
  IconExternalLink,
  IconCheck,
  IconBuilding,
  IconPlus,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useExternalInstitutions,
  useCreateSendOut,
} from "../../hooks/useSendOuts";
import { SendoutRequestType } from "../../services/sendOuts.service";
import { useAuthStore } from "../../store";
import { usePathologists } from "../../hooks/useReferenceData";
import {
  required,
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";
import MaterialPicker, {
  MaterialPickerSelection,
  SpecimenInfo,
} from "../send-outs/MaterialPicker";

// ==================== TYPES ====================

type Specimen = SpecimenInfo;

interface RequestExternalOpinionModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  specimens: Specimen[];
  onSuccess?: () => void;
  onAddInstitution?: () => void;
  duplicateConfirmed?: boolean;
}

interface ExternalOpinionFormValues {
  requestType: string | null;
  externalInstitutionId: string | null;
  reason: string;
  reportIncluded: boolean;
  requisitionIncluded: boolean;
  qaReviewerId: string | null;
  notes: string;
}

// ==================== CONSTANTS ====================

const BASE_REQUEST_TYPE_OPTIONS = [
  {
    value: SendoutRequestType.PHYSICIAN_REQUEST,
    label: "Ordering Physician Requesting Consultation",
  },
  {
    value: SendoutRequestType.GCP_CONSULT,
    label: "Pathologist Requires Expert Consultation",
  },
];

const EXTERNAL_INSTITUTION_OPTION = {
  value: SendoutRequestType.EXTERNAL_INSTITUTION,
  label: "External Request",
};

// ==================== COMPONENT ====================

export default function RequestExternalOpinionModal({
  opened,
  onClose,
  caseId,
  specimens,
  onSuccess,
  onAddInstitution,
  duplicateConfirmed = false,
}: RequestExternalOpinionModalProps) {
  const isUpdatingEditorRef = useRef(false);

  // Materials selection state
  const [materialsSelection, setMaterialsSelection] =
    useState<MaterialPickerSelection>({
      blockIds: new Set(),
      slideIds: new Set(),
    });
  const [materialsError, setMaterialsError] = useState<string | null>(null);

  const selectedSlideIds = materialsSelection.slideIds;
  const selectedBlockIds = materialsSelection.blockIds;

  const handleMaterialsChange = useCallback((next: MaterialPickerSelection) => {
    setMaterialsError(null);
    setMaterialsSelection(next);
  }, []);

  const { profile } = useAuthStore();
  const hasApprovePermission =
    profile?.permissions?.includes("approve_send_outs") ?? false;

  const requestTypeOptions = hasApprovePermission
    ? [...BASE_REQUEST_TYPE_OPTIONS, EXTERNAL_INSTITUTION_OPTION]
    : BASE_REQUEST_TYPE_OPTIONS;

  // Form
  const form = useForm<ExternalOpinionFormValues>({
    initialValues: {
      requestType: SendoutRequestType.PHYSICIAN_REQUEST as string | null,
      externalInstitutionId: null as string | null,
      reason: "",
      reportIncluded: true,
      requisitionIncluded: true,
      qaReviewerId: null as string | null,
      notes: "",
    },
    validate: {
      requestType: required("Request type is required"),
      externalInstitutionId: required("External institution is required"),
      reason: (val) => {
        const text = val?.replace(/<[^>]*>/g, "").trim();
        return text ? null : "Reason for consultation is required";
      },
      qaReviewerId: required("QA reviewer is required"),
    },
  });

  // Queries
  const { data: institutions, isLoading: isLoadingInstitutions } =
    useExternalInstitutions();
  const { data: pathologists = [] } = usePathologists();

  // Mutation
  const createSendOutMutation = useCreateSendOut();

  // Prepare institution options - sorted alphabetically
  const institutionOptions = useMemo(() => {
    if (!institutions) return [];
    return institutions
      .map((inst) => ({
        value: inst.id,
        label: inst.shortName ? `${inst.name} (${inst.shortName})` : inst.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [institutions]);

  // QA reviewer options - sorted alphabetically
  const qaReviewerOptions = useMemo(() => {
    return pathologists
      .map((p) => ({
        value: p.id,
        label: p.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [pathologists]);

  // Get selected institution details
  const selectedInstitution = useMemo(() => {
    const id = form.values.externalInstitutionId;
    if (!id || !institutions) return null;
    return institutions.find((i) => i.id === id) || null;
  }, [form.values.externalInstitutionId, institutions]);

  // Find default QA reviewer from database flag
  const defaultQaReviewerId = useMemo(() => {
    const defaultReviewer = pathologists.find((p) => p.isDefaultQaReviewer);
    return defaultReviewer?.id || "";
  }, [pathologists]);

  // TipTap editors
  const reasonEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Describe why an external opinion is needed...",
      }),
      Underline,
    ],
    content: "",
    onUpdate: ({ editor: ed }) => {
      if (isUpdatingEditorRef.current) return;
      form.setFieldValue("reason", ed.getHTML());
    },
  });

  const notesEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Any additional notes or instructions...",
      }),
      Underline,
    ],
    content: "",
    onUpdate: ({ editor: ed }) => {
      if (isUpdatingEditorRef.current) return;
      form.setFieldValue("notes", ed.getHTML());
    },
  });

  useEffect(() => {
    if (!reasonEditor) return;
    const formVal = form.values.reason || "";
    if (reasonEditor.getHTML() !== formVal) {
      isUpdatingEditorRef.current = true;
      reasonEditor.commands.setContent(formVal);
      isUpdatingEditorRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.reason, reasonEditor]);

  useEffect(() => {
    if (!notesEditor) return;
    const formVal = form.values.notes || "";
    if (notesEditor.getHTML() !== formVal) {
      isUpdatingEditorRef.current = true;
      notesEditor.commands.setContent(formVal);
      isUpdatingEditorRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.notes, notesEditor]);

  // Reset form and selection when modal opens
  useEffect(() => {
    if (opened) {
      form.reset();
      form.setFieldValue("qaReviewerId", defaultQaReviewerId);
      setMaterialsSelection({ blockIds: new Set(), slideIds: new Set() });
      setMaterialsError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultQaReviewerId]);

  // Handle form submission
  const handleSubmit = form.onSubmit(
    async (values: ExternalOpinionFormValues) => {
      // Validate at least some materials are selected
      if (selectedSlideIds.size === 0 && selectedBlockIds.size === 0) {
        setMaterialsError("Please select at least one slide or block to send");
        setTimeout(() => {
          const el = document.getElementById("materials-to-send");
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 50);
        return;
      }

      try {
        await createSendOutMutation.mutateAsync({
          caseId,
          requestType: values.requestType as SendoutRequestType,
          externalInstitutionId: values.externalInstitutionId!,
          reason: values.reason,
          slidesSent: Array.from(selectedSlideIds),
          blocksSent: Array.from(selectedBlockIds),
          reportIncluded: values.reportIncluded,
          requisitionIncluded: values.requisitionIncluded,
          qaReviewerId: values.qaReviewerId!,
          notes: values.notes || undefined,
          duplicateConfirmed,
        });

        notifications.show({
          title: "External Opinion Requested",
          message: "Send-out request has been created successfully.",
          color: "green",
          icon: <IconCheck size={16} />,
        });

        onSuccess?.();
        onClose();
      } catch (error: any) {
        showValidationNotification(
          error.message || "Failed to create external opinion request"
        );
      }
    },
    (validationErrors) => {
      // Delay to let error messages render before scrolling
      setTimeout(() => scrollToFirstError(form, validationErrors), 50);
    }
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconExternalLink size={20} />
          <Text fw={600}>Request External Opinion</Text>
        </Group>
      }
      size="xl"
      centered
      styles={{
        content: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
        body: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          overflow: "hidden",
          flex: 1,
        },
      }}
      closeOnClickOutside={false}
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <Box style={{ flex: 1, overflowY: "auto" }} p="md">
          <Stack gap="md">
            {/* Request Type */}
            <Select
              label="Request Type"
              placeholder="Select request type"
              data={requestTypeOptions}
              withAsterisk
              {...form.getInputProps("requestType")}
            />

            {/* External Institution Selection */}
            <div>
              <Select
                label="Destination Institution"
                placeholder={
                  isLoadingInstitutions
                    ? "Loading institutions..."
                    : "Select institution"
                }
                data={institutionOptions}
                withAsterisk
                searchable
                disabled={isLoadingInstitutions}
                rightSection={
                  isLoadingInstitutions ? <Loader size={16} /> : null
                }
                nothingFoundMessage="No institutions found"
                {...form.getInputProps("externalInstitutionId")}
              />
              {onAddInstitution && (
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconPlus size={14} />}
                  mt={4}
                  onClick={onAddInstitution}
                >
                  Add New Institution
                </Button>
              )}
            </div>

            {/* Selected Institution Info */}
            {selectedInstitution && (
              <Alert
                icon={<IconBuilding size={16} />}
                color="blue"
                variant="light"
              >
                <Text size="sm" fw={500}>
                  {selectedInstitution.name}
                </Text>
                {selectedInstitution.city && selectedInstitution.state && (
                  <Text size="xs" c="dimmed">
                    {selectedInstitution.city}, {selectedInstitution.state}
                  </Text>
                )}
                {selectedInstitution.specialties && (
                  <Text size="xs" c="dimmed">
                    Specialties: {selectedInstitution.specialties}
                  </Text>
                )}
                {selectedInstitution.averageTurnaroundDays && (
                  <Badge size="xs" variant="light" color="gray" mt={4}>
                    ~{selectedInstitution.averageTurnaroundDays} days turnaround
                  </Badge>
                )}
              </Alert>
            )}

            <Divider />

            {/* Materials to Send */}
            <div id="materials-to-send">
              <Text size="sm" fw={500} mb="xs">
                Materials to Send{" "}
                <Text span c="red">
                  *
                </Text>
              </Text>
              <MaterialPicker
                specimens={specimens}
                selection={materialsSelection}
                onChange={handleMaterialsChange}
                error={materialsError}
              />
            </div>

            <Divider />

            {/* Include Documents */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                Include Documents
              </Text>
              <Group>
                <Checkbox
                  label="Copy of GCP Report"
                  {...form.getInputProps("reportIncluded", {
                    type: "checkbox",
                  })}
                />
                <Checkbox
                  label="Original Requisition"
                  {...form.getInputProps("requisitionIncluded", {
                    type: "checkbox",
                  })}
                />
              </Group>
            </div>

            <Divider />

            {/* QA Reviewer */}
            <Select
              label="QA Reviewer"
              placeholder="Select QA reviewer"
              description="Default reviewer - can be changed when report is received"
              data={qaReviewerOptions}
              withAsterisk
              searchable
              {...form.getInputProps("qaReviewerId")}
            />

            <Divider />

            {/* Reason */}
            <Box id="reason">
              <Text size="sm" fw={500} mb={4}>
                Reason for Consultation{" "}
                <Text span c="red">
                  *
                </Text>
              </Text>
              <RichTextEditor
                editor={reasonEditor}
                data-path="reason"
                aria-invalid={!!form.errors.reason}
                aria-describedby={
                  form.errors.reason ? "reason-error" : undefined
                }
                style={
                  form.errors.reason
                    ? {
                        border: "1px solid var(--mantine-color-red-6)",
                        backgroundColor: "var(--mantine-color-red-light)",
                      }
                    : undefined
                }
              >
                <RichTextEditor.Toolbar>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content />
              </RichTextEditor>
              {form.errors.reason && (
                <Text size="xs" c="red" mt={4} id="reason-error">
                  {form.errors.reason}
                </Text>
              )}
            </Box>

            {/* Additional Notes */}
            <Box id="notes">
              <Text size="sm" fw={500} mb={4}>
                Additional Notes
              </Text>
              <RichTextEditor editor={notesEditor}>
                <RichTextEditor.Toolbar>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content />
              </RichTextEditor>
            </Box>
          </Stack>
        </Box>

        {/* Sticky Footer */}
        <Group
          justify="flex-end"
          p="md"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-3)",
          }}
        >
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={createSendOutMutation.isPending}
            leftSection={<IconExternalLink size={16} />}
            color="orange"
          >
            Submit Request
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
